import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Article } from '@/types/article';
import { QuestionCatalog } from '@/services/questionCatalogs';
import { useAuth } from '@/hooks/useClerkAuth';
import ArticleCard from '@/components/ArticleCard';
import SentenceReader from '@/components/SentenceReader';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Search, BookOpen, Plus, Play, Edit, Book, Users } from 'lucide-react';

interface ArticlesProps {
  articles: Article[];
  catalogs?: QuestionCatalog[];
  onNavigateToAdmin: () => void;
  showAdminButton?: boolean;
  requireAuthForReading?: boolean;
}

const Articles = ({
  articles,
  catalogs = [],
  onNavigateToAdmin,
  showAdminButton = true,
  requireAuthForReading = false,
}: ArticlesProps) => {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [articlesList, setArticlesList] = useState<Article[]>(articles);

  // Update local articles list when props change
  React.useEffect(() => {
    setArticlesList(articles);
  }, [articles]);

  const filteredArticles = articlesList.filter(
    article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleArticleClick = (article: Article) => {
    if (isLoading) {
      return;
    }

    setSelectedArticle(article);
  };

  const handleCloseReader = () => {
    setSelectedArticle(null);
  };

  const handleDeleteArticle = (articleId: string) => {
    setArticlesList(prev => prev.filter(article => article.id !== articleId));
  };

  const handleStartPractice = (catalogId: string) => {
    // Allow both authenticated users and trial users to start practice
    navigate(`/questions/${catalogId}/practice`);
  };

  const handleEditCatalog = (catalogId: string) => {
    if (!user || !showAdminButton) {
      navigate('/sign-in');
      return;
    }
    navigate(`/questions/${catalogId}/edit`);
  };

  const renderCatalogCard = (catalog: QuestionCatalog) => (
    <Card
      key={catalog.id}
      className="group hover:shadow-lg transition-all duration-300 border border-learning-muted/40 hover:border-learning-primary/40"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl mb-2 group-hover:text-learning-primary transition-colors">
              {catalog.name}
            </CardTitle>
            {catalog.description && (
              <CardDescription className="text-muted-foreground">
                {catalog.description}
              </CardDescription>
            )}
          </div>
          {catalog.imageUrl && (
            <img
              src={catalog.imageUrl}
              alt={catalog.name}
              className="w-16 h-16 rounded-lg object-cover ml-4"
            />
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            <span>
              {catalog.totalQuestions} {t('articles.questions')}
            </span>
          </div>
          {catalog.stateSpecificQuestions > 0 && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>
                {catalog.stateSpecificQuestions} {t('articles.stateSpecific')}
              </span>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            onClick={() => handleStartPractice(catalog.id)}
            className="flex-1 bg-learning-primary hover:bg-learning-primary/90"
          >
            <Play className="h-4 w-4 mr-2" />
            {t('articles.startPractice')}
          </Button>

          {showAdminButton && (
            <Button
              variant="outline"
              onClick={() => handleEditCatalog(catalog.id)}
              className="border-learning-muted hover:border-learning-primary hover:text-learning-primary"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (selectedArticle) {
    return (
      <SentenceReader
        sentences={selectedArticle.sentences}
        onClose={handleCloseReader}
        originalLink={selectedArticle.originalLink}
        title={selectedArticle.title}
        requireAuth={requireAuthForReading}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-hero text-primary-foreground">
        <div className="container max-w-6xl mx-auto px-6 py-12">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('header.title')}
            </h1>
            <p className="text-lg text-primary-foreground/90 max-w-2xl mx-auto">
              {t('header.subtitle')}
            </p>
            {!user && (
              <div className="mt-8">
                <div className="mb-4">
                  <p className="text-primary-foreground/80 text-base">
                    {t('articles.signUpFreePrompt')}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate('/sign-up')}
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-primary-foreground border-white/30"
                  >
                    {t('auth.signUpFree')}
                  </Button>
                  <Button
                    onClick={() => navigate('/sign-in')}
                    variant="outline"
                    className="bg-transparent border-white/30 text-primary-foreground hover:bg-white/10"
                  >
                    {t('auth.signIn')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container max-w-6xl mx-auto px-6 py-8 flex-grow">
        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder={t('search.placeholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/70 backdrop-blur-sm"
            />
          </div>
        </div>

        {/* Articles Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            {t('articles.readingArticles')}
          </h2>
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map(article => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  onClick={() => handleArticleClick(article)}
                  onDelete={handleDeleteArticle}
                />
              ))}
            </div>
          ) : articlesList.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {t('articles.noArticles')}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t('articles.noArticlesDescription')}
              </p>
              {showAdminButton ? (
                <Button
                  onClick={onNavigateToAdmin}
                  className="bg-gradient-hero"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('articles.createFirstArticle')}
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/auth?mode=signup')}
                  className="bg-gradient-hero"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('articles.signUpToStartLearning')}
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {t('articles.noResults')}
              </h3>
              <p className="text-muted-foreground">
                {t('articles.noResultsDescription')}
              </p>
            </div>
          )}
        </div>

        {/* Questions Section */}
        {catalogs.length > 0 && (
          <div className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                {t('articles.practiceQuestions')}
              </h2>
              <Button
                onClick={() => navigate('/questions')}
                variant="outline"
                className="bg-white/70 backdrop-blur-sm"
              >
                {t('articles.viewAllQuestions')}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {catalogs.map(renderCatalogCard)}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Articles;
